import { z } from "zod";
import type { Area } from "./permissions.js";

/**
 * Anpassbare Übersicht: Katalog der Widgets und Layout (Reihenfolge, Breite).
 * Je Person speicherbar; der Kunden-Admin legt den Team-Standard fest. Rechte und gebuchte Module bleiben die
 * Grenze – ein Widget ohne Leserecht/Modul wird weder angeboten noch angezeigt.
 */
export const WIDGET_KEYS = ["kpis", "action_items", "news", "calendar", "order_flow", "channels", "reorder", "slow_movers", "inbounds", "production", "profit"] as const;
export type WidgetKey = (typeof WIDGET_KEYS)[number];

export interface WidgetDef {
  title: string;
  description: string;
  /** Standardbreite */
  size: "full" | "half";
  /** braucht Leserecht in diesem Bereich (eines davon) */
  areas?: Area[];
  /** braucht diese Modul-Funktion */
  module?: [string, string];
}

export const WIDGETS: Record<WidgetKey, WidgetDef> = {
  kpis: { title: "Kennzahlen", description: "Aufträge der letzten 24 h, zurückgehaltene Aufträge, Datenqualität, blockierte Artikel", size: "full", areas: ["orders", "products"] },
  action_items: { title: "Handlungsbedarf", description: "Was gerade eure Aufmerksamkeit braucht – das Dringendste zuerst", size: "half" },
  news: { title: "Neuigkeiten von Logiful", description: "Ankündigungen und wichtige Hinweise", size: "half" },
  calendar: { title: "Kalender", description: "Feiertage im Lager, feste Anliefertermine, erwartete Anlieferungen", size: "half" },
  order_flow: { title: "Auftragsfluss", description: "Eingang, zurückgehalten, übertragen, versendet", size: "half", areas: ["orders"] },
  channels: { title: "Shops", description: "Verbundene Shops und Marktplätze mit letztem Abgleich", size: "half" },
  reorder: { title: "Nachbestellen", description: "Artikel, die ausverkauft sind oder bald nachbestellt werden müssen", size: "half", areas: ["stock"] },
  slow_movers: { title: "Ladenhüter", description: "Was zu lange liegt und wie viel Kapital darin steckt", size: "half", areas: ["stock"], module: ["analytics", "slow_movers"] },
  inbounds: { title: "Offene Anlieferungen", description: "Angemeldete Wareneingänge mit Termin und Fortschritt", size: "half", areas: ["inbounds"] },
  production: { title: "Produktion", description: "Offene Produktionsaufträge – was auf Komponenten, Muster-Freigabe oder Start wartet", size: "half", areas: ["production"], module: ["production", "orders"] },
  profit: { title: "Profit Center", description: "Umsatz, Deckungsbeitrag und Marge der letzten 30 Tage", size: "half", areas: ["profit"], module: ["profit", "margins"] },
};

export const dashboardLayoutSchema = z.object({
  widgets: z
    .array(z.object({ id: z.enum(WIDGET_KEYS), size: z.enum(["full", "half"]) }))
    .max(WIDGET_KEYS.length)
    .refine((w) => new Set(w.map((x) => x.id)).size === w.length, "Jedes Widget nur einmal"),
});
export type DashboardLayout = z.infer<typeof dashboardLayoutSchema>;

/** Standard, solange weder Team noch Person etwas festgelegt haben */
export const DEFAULT_DASHBOARD: DashboardLayout = {
  widgets: [
    { id: "kpis", size: "full" },
    { id: "action_items", size: "half" },
    { id: "news", size: "half" },
    { id: "calendar", size: "half" },
    { id: "order_flow", size: "half" },
    { id: "channels", size: "half" },
  ],
};

/** Darf diese Person das Widget sehen? (Leserecht in einem der Bereiche, Modul-Funktion gebucht) */
export function widgetAllowed(key: WidgetKey, can: (area: Area) => boolean, hasFeature: (module: string, feature: string) => boolean): boolean {
  const w = WIDGETS[key];
  if (w.areas && !w.areas.some(can)) return false;
  if (w.module && !hasFeature(w.module[0], w.module[1])) return false;
  return true;
}

/** Gespeichertes Layout tolerant lesen (unbekannte/doppelte Widgets aus älteren Ständen fallen weg) */
export function parseDashboardLayout(raw: unknown): DashboardLayout | null {
  const list = (raw as { widgets?: unknown[] } | null)?.widgets;
  if (!Array.isArray(list)) return null;
  const seen = new Set<string>();
  const widgets = list.flatMap((w) => {
    const p = z.object({ id: z.enum(WIDGET_KEYS), size: z.enum(["full", "half"]) }).safeParse(w);
    if (!p.success || seen.has(p.data.id)) return [];
    seen.add(p.data.id);
    return [p.data];
  });
  return { widgets };
}
